(function () {
  'use strict';
  const tag = 'acf-brazil-delivery';
  if (customElements.get(tag)) return;

  const keys = ['_acf_brazil_tax_id', '_acf_is_business', '_acf_state_tax_id', '_acf_registration_state_code'];
  const messages = {
    missingTax: 'Informe o CPF ou CNPJ do destinatário para a entrega no Brasil.',
    missingBusiness: 'Para uma entrega a pessoa jurídica no Brasil, informe a Inscrição Estadual (IE) e a UF de registro.',
    tooLong: 'Use no máximo 32 caracteres para o CPF ou CNPJ e a IE, e 35 para a UF de registro.',
    pending: 'Salvando os dados para a entrega…',
    saved: 'Dados para a entrega salvos.',
    failed: 'Não foi possível salvar os dados. Tente novamente antes de finalizar a compra.',
    cleared: 'Dados de entrega removidos. Preencha os campos para finalizar a compra.'
  };

  function normalize(values) {
    const text = (value) => String(value ?? '').trim();
    const business = values.business === true;
    return {
      tax: text(values.tax),
      business,
      ie: business ? text(values.ie) : '',
      state: business ? text(values.state) : ''
    };
  }

  function validation(values) {
    const value = normalize(values);
    if (!value.tax) return messages.missingTax;
    if ([...value.tax].length > 32 || [...value.ie].length > 32 || [...value.state].length > 35) return messages.tooLong;
    if (value.business && (!value.ie || !value.state)) return messages.missingBusiness;
    return '';
  }

  function attributes(values) {
    const value = normalize(values);
    return {
      [keys[0]]: value.tax,
      [keys[1]]: value.business ? 'true' : 'false',
      [keys[2]]: value.ie,
      [keys[3]]: value.state
    };
  }

  function responseMatches(cart, expected, requireShipping = true) {
    if (!cart?.attributes || (requireShipping && cart.requires_shipping !== true)) return false;
    return keys.every((key) => String(cart.attributes[key] ?? '') === expected[key]);
  }

  const state = {
    values: null, revision: 0, confirmed: -1, dirty: false, clearRequested: false,
    saving: false, verifying: false, verifyId: 0, queued: false, timer: null,
    message: '', components: new Set()
  };

  function connected() {
    return [...state.components].filter((component) => component.isConnected);
  }

  function ready() {
    return connected().length > 0 && state.values !== null && !validation(state.values) && state.confirmed === state.revision && !state.saving;
  }

  function broadcast() {
    const components = connected();
    const canCheckout = components.length > 0 && ready();
    for (const component of components) {
      component.writeValues(state.values);
      const status = component.querySelector('[data-acf-status]');
      if (status && status.textContent !== state.message) status.textContent = state.message;
      const save = component.querySelector('[data-acf-save]');
      if (save) save.disabled = state.saving || state.verifying;
    }
    document.querySelectorAll('[data-acf-brazil-checkout]').forEach((button) => {
      button.disabled = !canCheckout;
      button.setAttribute('aria-disabled', String(!canCheckout));
    });
  }

  function endpoint(component, name) {
    const root = component.dataset.cartRoot;
    if (!root || !root.startsWith('/') || root.startsWith('//')) throw new Error('Invalid cart route');
    return root.replace(/\/$/, '') + (name ? '/cart/' + name + '.js' : '/cart.js');
  }

  // Writes are serialized: an older request can never overwrite a newer edit last.
  async function saveCurrent() {
    if (!connected().length) return;
    clearTimeout(state.timer);
    state.timer = null;
    const problem = state.clearRequested ? '' : validation(state.values);
    if (problem) {
      state.confirmed = -1;
      state.message = problem;
      broadcast();
      return;
    }
    if (state.saving) {
      state.queued = true;
      return;
    }
    const component = connected()[0];
    const revision = state.revision;
    const clearing = state.clearRequested;
    const expected = clearing ? Object.fromEntries(keys.map((key) => [key, ''])) : attributes(state.values);
    state.saving = true;
    state.queued = false;
    state.confirmed = -1;
    state.message = messages.pending;
    broadcast();
    try {
      const result = await fetch(endpoint(component, 'update'), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: expected })
      });
      if (!result.ok) throw new Error('Cart save failed');
      const cart = await result.json();
      if (!responseMatches(cart, expected, !clearing)) throw new Error('Cart save not confirmed');
      if (revision === state.revision) {
        state.confirmed = clearing ? -1 : revision;
        state.dirty = false;
        state.clearRequested = false;
        state.message = clearing ? messages.cleared : messages.saved;
      }
    } catch (_error) {
      if (revision === state.revision) state.message = messages.failed;
    } finally {
      state.saving = false;
      const newerEdit = revision !== state.revision;
      const again = state.queued || newerEdit;
      state.queued = false;
      broadcast();
      if (again && connected().length) void saveCurrent();
    }
  }

  // Existing Liquid values are read back before enabling payment. No identifiers
  // are generated and there is no POST until a customer interacts with the fields.
  async function verifySaved(component) {
    if (state.verifying || state.saving || state.dirty || validation(state.values)) return;
    const revision = state.revision;
    const expected = attributes(state.values);
    const verifyId = ++state.verifyId;
    state.verifying = true;
    broadcast();
    try {
      const result = await fetch(endpoint(component, ''), {
        credentials: 'same-origin', headers: { 'Accept': 'application/json' }
      });
      if (!result.ok) throw new Error('Cart read failed');
      const cart = await result.json();
      if (revision === state.revision && !state.saving && responseMatches(cart, expected)) {
        state.confirmed = revision;
        state.message = messages.saved;
      } else if (revision === state.revision && !state.saving) {
        state.confirmed = -1;
        state.message = messages.failed;
      }
    } catch (_error) {
      if (revision === state.revision && !state.saving) state.message = messages.failed;
    } finally {
      if (verifyId === state.verifyId) state.verifying = false;
      broadcast();
    }
  }

  function edit(component) {
    state.values = normalize(component.readValues());
    state.revision += 1;
    state.confirmed = -1;
    state.dirty = true;
    state.clearRequested = false;
    state.verifyId += 1;
    state.verifying = false;
    state.message = '';
    clearTimeout(state.timer);
    broadcast();
    state.timer = setTimeout(() => void saveCurrent(), 450);
  }

  function clearCurrent() {
    if (!connected().length) return;
    state.values = normalize({});
    state.revision += 1;
    state.confirmed = -1;
    state.dirty = true;
    state.clearRequested = true;
    state.verifyId += 1;
    state.verifying = false;
    clearTimeout(state.timer);
    state.timer = null;
    broadcast();
    void saveCurrent();
  }

  class BrazilDelivery extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      // When the class is already registered, the HTML parser can connect a
      // new element before its inputs have been parsed. Wait for complete
      // fields rather than hydrating an empty snapshot over saved attributes.
      const fieldsComplete = ['tax', 'business', 'ie', 'state'].every((field) =>
        this.querySelector('[data-acf-field="' + field + '"]')) && this.querySelector('[data-acf-status]');
      if (!fieldsComplete) {
        if (!this._waitingObserver) {
          this._waitingObserver = new MutationObserver(() => {
            if (this.isConnected) this.connectedCallback();
          });
          this._waitingObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
      this._waitingObserver?.disconnect();
      this._waitingObserver = null;
      this._connected = true;
      if (state.values === null) state.values = normalize(this.readValues());
      state.components.add(this);
      this._input = (event) => {
        if (event.target.matches('[data-acf-field]')) edit(this);
      };
      this._click = (event) => {
        if (event.target.closest('[data-acf-clear]')) {
          clearCurrent();
          return;
        }
        if (!event.target.closest('[data-acf-save]')) return;
        const problem = validation(state.values);
        if (problem) {
          state.message = problem;
          broadcast();
          this.querySelector('[data-acf-field="tax"]')?.focus();
          return;
        }
        void saveCurrent();
      };
      this.addEventListener('input', this._input);
      this.addEventListener('click', this._click);
      // Theme morphing may keep the custom element while replacing its children
      // or its adjacent checkout button. Delegated listeners and this observer
      // restore the current values and the payment guard after either operation.
      this._observer = new MutationObserver(() => broadcast());
      this._observer.observe(this.parentElement, { childList: true, subtree: true });
      broadcast();
      if (!state.dirty) void verifySaved(this);
      else if (!state.saving) {
        clearTimeout(state.timer);
        state.timer = setTimeout(() => void saveCurrent(), 450);
      }
    }

    disconnectedCallback() {
      this._waitingObserver?.disconnect();
      this._waitingObserver = null;
      this._connected = false;
      state.components.delete(this);
      this.removeEventListener('input', this._input);
      this.removeEventListener('click', this._click);
      this._observer?.disconnect();
      if (!connected().length) {
        clearTimeout(state.timer);
        state.timer = null;
        // A morph disconnect/reconnect in the same task preserves unsaved edits.
        queueMicrotask(() => {
          if (!connected().length && !state.saving) {
            state.values = null;
            state.revision += 1;
            state.dirty = false;
            state.clearRequested = false;
            state.confirmed = -1;
            state.verifyId += 1;
            state.verifying = false;
            state.message = '';
          }
        });
      }
    }

    readValues() {
      return {
        tax: this.querySelector('[data-acf-field="tax"]')?.value ?? '',
        business: this.querySelector('[data-acf-field="business"]')?.checked === true,
        ie: this.querySelector('[data-acf-field="ie"]')?.value ?? '',
        state: this.querySelector('[data-acf-field="state"]')?.value ?? ''
      };
    }

    writeValues(values) {
      if (!values) return;
      for (const field of ['tax', 'ie', 'state']) {
        const input = this.querySelector('[data-acf-field="' + field + '"]');
        if (input && input.value !== values[field]) input.value = values[field];
      }
      const business = this.querySelector('[data-acf-field="business"]');
      if (business) business.checked = values.business;
      const group = this.querySelector('[data-acf-business-fields]');
      if (group) group.hidden = !values.business;
      for (const field of ['ie', 'state']) {
        const input = this.querySelector('[data-acf-field="' + field + '"]');
        if (input) {
          input.disabled = !values.business;
          input.required = values.business;
        }
      }
    }
  }

  function blockUnconfirmed(event) {
    const checkoutClick = event.type === 'click' && event.target.closest?.('[data-acf-brazil-checkout]');
    const checkoutSubmit = event.type === 'submit' && event.target.id === 'cart-form' &&
      (!event.submitter || event.submitter.name === 'checkout') &&
      document.querySelector('[data-acf-brazil-checkout]');
    if (!(checkoutClick || checkoutSubmit) || ready()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    state.message = validation(state.values ?? {}) || (state.saving ? messages.pending : messages.failed);
    broadcast();
    connected()[0]?.querySelector('[data-acf-field="tax"]')?.focus();
  }

  document.addEventListener('click', blockUnconfirmed, true);
  document.addEventListener('submit', blockUnconfirmed, true);
  customElements.define(tag, BrazilDelivery);
})();
